// ============================================================================
// auth — SessionProvider
// Owner: Claude 3 (Frontend & UX).
// Purpose: React context exposing the current eDMS session + idle timeout.
//          15-minute idle auto-logout per AUTH-008.
// URS rows: AUTH-001, AUTH-008.
// ============================================================================
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { initiateLogin, logout as oidcLogout, readStoredSession, refreshSession, Session } from './oidcClient';

type SessionContextValue = {
  session: Session | null;
  loading: boolean;
  login: (returnTo: string) => Promise<void>;
  logout: () => Promise<void>;
  setSession: (s: Session | null) => void;
};

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;          // AUTH-008
const REFRESH_LEAD_MS = 30 * 1000;                // refresh 30s before expiry

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const lastActivityRef = useRef<number>(Date.now());

  // Hydrate from sessionStorage on mount.
  useEffect(() => {
    const stored = readStoredSession();
    if (stored && stored.expiresAt > Date.now()) setSession(stored);
    setLoading(false);
  }, []);

  // Refresh timer.
  useEffect(() => {
    if (!session) return;
    const ms = Math.max(1_000, session.expiresAt - Date.now() - REFRESH_LEAD_MS);
    const t = window.setTimeout(async () => {
      try {
        const refreshed = await refreshSession();
        setSession(refreshed);
      } catch (e) {
        // Refresh failed -> force logout
        setSession(null);
      }
    }, ms);
    return () => window.clearTimeout(t);
  }, [session]);

  // Idle-timeout watcher — passive listeners; cleaned up on unmount.
  useEffect(() => {
    if (!session) return;
    const onActivity = () => { lastActivityRef.current = Date.now(); };
    const events: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'touchstart', 'scroll'];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    const interval = window.setInterval(() => {
      if (Date.now() - lastActivityRef.current > IDLE_TIMEOUT_MS) {
        oidcLogout().finally(() => setSession(null));
      }
    }, 30 * 1000);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.clearInterval(interval);
    };
  }, [session]);

  const login = useCallback(async (returnTo: string) => {
    await initiateLogin(returnTo);
  }, []);

  const logout = useCallback(async () => {
    await oidcLogout();
    setSession(null);
  }, []);

  return (
    <SessionContext.Provider value={{ session, loading, login, logout, setSession }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
